/**
 * EditableTimeCell - Inline time editor
 *
 * Click to edit arrival or departure time with conflict warnings.
 * Snaps to 15-minute buckets.
 */

import { Box, TextField, Typography } from "@mui/material";
import { format } from "date-fns";
import React, { useState } from "react";
import { TableRow as PlacementRow } from "../utils/buildTableRows";
import { ScheduleChange } from "./SpotOccupancyGantt";

interface EditableTimeCellProps {
  row: PlacementRow;
  field: "arrival" | "departure";
  enableEditing: boolean;
  onScheduleChanged?: (changes: ScheduleChange[]) => void;
}

export const EditableTimeCell: React.FC<EditableTimeCellProps> = ({
  row,
  field,
  enableEditing,
  onScheduleChanged,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState("");

  const currentTime = field === "arrival" ? row.arrivalTime : row.departureTime;

  // Handle click to enter edit mode
  const handleClick = (e: React.MouseEvent) => {
    if (!enableEditing) return;
    e.stopPropagation(); // Prevent row expansion
    setIsEditing(true);
    // Set initial value in HH:mm format
    setTempValue(format(currentTime, "HH:mm"));
  };

  // Handle time change
  const handleChange = (newTimeStr: string) => {
    if (!onScheduleChanged) return;

    try {
      // Parse time string (HH:mm)
      const [hours, minutes] = newTimeStr.split(":").map(Number);
      if (isNaN(hours) || isNaN(minutes)) return;

      // Snap to 15-minute intervals
      const snappedMinutes = Math.round(minutes / 15) * 15;
      const snappedHours = snappedMinutes === 60 ? hours + 1 : hours;
      const finalMinutes = snappedMinutes === 60 ? 0 : snappedMinutes;

      // Calculate bucket offset from current time
      const currentDate =
        field === "arrival" ? row.arrivalTime : row.departureTime;
      const newDate = new Date(currentDate);
      newDate.setHours(snappedHours, finalMinutes, 0, 0);

      // Calculate bucket from date difference
      const diffMs = newDate.getTime() - row.arrivalTime.getTime();
      const diffBuckets = Math.round(diffMs / (15 * 60 * 1000));

      const newArrivalBucket =
        field === "arrival"
          ? row.arrivalBucket + diffBuckets
          : row.arrivalBucket;
      const newDepartureBucket =
        field === "departure"
          ? row.departureBucket + diffBuckets
          : row.departureBucket;

      // Emit schedule change event
      const change: ScheduleChange = {
        aircraftId: row.aircraftId,
        tailNumber: row.tailNumber,
        originalSpotId: row.spotIds[0],
        newSpotId: row.spotIds[0],
        originalArrivalBucket: row.arrivalBucket,
        originalDepartureBucket: row.departureBucket,
        newArrivalBucket,
        newDepartureBucket,
        segmentIndex: null,
      };

      onScheduleChanged([change]);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to parse time:", error);
      setIsEditing(false);
    }
  };

  // Handle blur or Enter key
  const handleBlur = () => {
    if (tempValue) {
      handleChange(tempValue);
    } else {
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleBlur();
    } else if (e.key === "Escape") {
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <TextField
        value={tempValue}
        onChange={(e) => setTempValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoFocus
        placeholder="HH:mm"
        size="small"
        sx={{
          width: 80,
          "& .MuiOutlinedInput-root": {
            fontSize: "11px",
            fontFamily: "SF Mono, Consolas, monospace",
          },
          "& .MuiOutlinedInput-input": {
            py: 0.5,
            px: 1,
          },
        }}
      />
    );
  }

  // Normal display mode
  return (
    <Box
      onClick={handleClick}
      sx={{
        cursor: enableEditing ? "pointer" : "default",
        "&:hover": enableEditing
          ? {
              "& .edit-hint": {
                opacity: 1,
              },
            }
          : {},
      }}
    >
      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: 500,
          color: "text.primary",
          fontFamily: "SF Mono, Consolas, monospace",
        }}
      >
        {format(currentTime, "MMM d, HH:mm")}
      </Typography>
    </Box>
  );
};
